import type { Locale } from "./types";

/** 所有 init* 返回一个清理函数，重新挂载（切换语言）前统一调用，避免监听器泄漏。 */
export type Cleanup = () => void;

const prefersReducedMotion = () =>
  typeof matchMedia === "function" &&
  matchMedia("(prefers-reduced-motion: reduce)").matches;

/** 移动端汉堡菜单开合 */
export function initNav(root: HTMLElement): Cleanup {
  const toggle = root.querySelector<HTMLButtonElement>(".menu-toggle");
  const panel = root.querySelector<HTMLElement>("#navlinks");
  if (!toggle || !panel) return () => {};

  const close = () => {
    panel.classList.remove("open");
    toggle.setAttribute("aria-expanded", "false");
  };
  const onToggle = () => {
    const open = panel.classList.toggle("open");
    toggle.setAttribute("aria-expanded", String(open));
  };
  const onPanelClick = (e: Event) => {
    if ((e.target as HTMLElement).closest("a")) close();
  };

  toggle.addEventListener("click", onToggle);
  panel.addEventListener("click", onPanelClick);
  return () => {
    toggle.removeEventListener("click", onToggle);
    panel.removeEventListener("click", onPanelClick);
  };
}

/**
 * 滚动入场动画：元素进入视口时加 .in（尊重 prefers-reduced-motion）。
 * 首屏已在视口内的元素立即显示，不依赖 IntersectionObserver 的首帧回调——
 * 否则在不主动合成帧的环境（部分无头/后台标签页）里内容会卡在不可见态。
 */
export function initReveal(root: HTMLElement): Cleanup {
  const items = Array.from(root.querySelectorAll<HTMLElement>("[data-reveal]"));
  if (prefersReducedMotion() || typeof IntersectionObserver === "undefined") {
    items.forEach((el) => el.classList.add("in"));
    return () => {};
  }
  const vh = window.innerHeight || document.documentElement.clientHeight;
  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add("in");
          io.unobserve(entry.target);
        }
      }
    },
    { rootMargin: "0px 0px -10% 0px", threshold: 0.1 },
  );
  for (const el of items) {
    const rect = el.getBoundingClientRect();
    if (rect.top < vh && rect.bottom > 0) {
      el.classList.add("in"); // 已在视口内：立即显示
    } else {
      io.observe(el); // 视口外：滚动到时再揭示
    }
  }
  return () => io.disconnect();
}

/** 滚动高亮：当前视口所在板块对应的导航项加 .active */
export function initScrollSpy(root: HTMLElement): Cleanup {
  const links = Array.from(root.querySelectorAll<HTMLAnchorElement>(".navlinks a"));
  const map = new Map<string, HTMLAnchorElement>();
  const sections: HTMLElement[] = [];
  for (const link of links) {
    const id = link.getAttribute("href")?.replace("#", "");
    if (!id) continue;
    const sec = document.getElementById(id);
    if (sec) {
      map.set(id, link);
      sections.push(sec);
    }
  }
  if (!sections.length || typeof IntersectionObserver === "undefined") return () => {};

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        map.forEach((a) => a.classList.remove("active"));
        map.get(entry.target.id)?.classList.add("active");
      }
    },
    { rootMargin: "-45% 0px -50% 0px" },
  );
  sections.forEach((s) => io.observe(s));
  return () => io.disconnect();
}

/** 返回顶部按钮：滚动超过一屏后显现 */
export function initBackToTop(root: HTMLElement): Cleanup {
  const btn = root.querySelector<HTMLButtonElement>(".to-top");
  if (!btn) return () => {};
  const onScroll = () => {
    btn.classList.toggle("show", window.scrollY > window.innerHeight * 0.6);
  };
  const onClick = () =>
    window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? "auto" : "smooth" });
  window.addEventListener("scroll", onScroll, { passive: true });
  btn.addEventListener("click", onClick);
  onScroll();
  return () => {
    window.removeEventListener("scroll", onScroll);
    btn.removeEventListener("click", onClick);
  };
}

/**
 * 联系表单：纯前端校验 + 成功提示。
 * 目前不落库；接后端时把 onSubmit 里的提示换成 fetch(POST) 即可。
 */
export function initContactForm(
  root: HTMLElement,
  messages: { success: string; invalid: string },
): Cleanup {
  const form = root.querySelector<HTMLFormElement>(".contact-form");
  const status = root.querySelector<HTMLElement>(".form-msg");
  if (!form || !status) return () => {};

  const onSubmit = (e: Event) => {
    e.preventDefault();
    if (!form.checkValidity()) {
      status.textContent = messages.invalid;
      status.dataset.state = "error";
      return;
    }
    // TODO(接后端): await fetch("/api/contact", { method: "POST", body: new FormData(form) })
    status.textContent = messages.success;
    status.dataset.state = "ok";
    form.reset();
  };
  form.addEventListener("submit", onSubmit);
  return () => form.removeEventListener("submit", onSubmit);
}

/**
 * 邮件订阅：纯前端校验 + 成功提示。
 * 接后端时把提示替换为 fetch(POST /api/newsletter) 即可。
 */
export function initNewsletter(
  root: HTMLElement,
  messages: { success: string; invalid: string },
): Cleanup {
  const form = root.querySelector<HTMLFormElement>(".newsletter-form");
  const status = root.querySelector<HTMLElement>(".newsletter-msg");
  if (!form || !status) return () => {};

  const onSubmit = (e: Event) => {
    e.preventDefault();
    if (!form.checkValidity()) {
      status.textContent = messages.invalid;
      status.dataset.state = "error";
      return;
    }
    // TODO(接后端): await fetch("/api/newsletter", { method: "POST", body: new FormData(form) })
    status.textContent = messages.success;
    status.dataset.state = "ok";
    form.reset();
  };
  form.addEventListener("submit", onSubmit);
  return () => form.removeEventListener("submit", onSubmit);
}

const THEME_STORAGE_KEY = "dohorizon:theme";
type ThemeOverride = "light" | "dark" | null;

function readStoredTheme(): ThemeOverride {
  try {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    return saved === "light" || saved === "dark" ? saved : null;
  } catch {
    return null;
  }
}

function applyTheme(theme: ThemeOverride): void {
  if (theme) document.documentElement.setAttribute("data-theme", theme);
  else document.documentElement.removeAttribute("data-theme");
}

/** 主题切换：手动覆盖跟随系统的深浅色，选择持久化到 localStorage */
export function initThemeToggle(root: HTMLElement): Cleanup {
  const btn = root.querySelector<HTMLButtonElement>(".theme-btn");
  if (!btn) return () => {};

  let override = readStoredTheme();
  applyTheme(override);

  const onClick = () => {
    const prefersDark =
      typeof matchMedia === "function" && matchMedia("(prefers-color-scheme: dark)").matches;
    const current = override ?? (prefersDark ? "dark" : "light");
    override = current === "dark" ? "light" : "dark";
    applyTheme(override);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, override);
    } catch {
      /* 忽略隐私模式等写入失败 */
    }
  };
  btn.addEventListener("click", onClick);
  return () => btn.removeEventListener("click", onClick);
}

/** 语言切换：点击后回调 onChange(newLocale)，由 main.ts 负责重新挂载 */
export function initLangSwitch(
  root: HTMLElement,
  onChange: (locale: Locale) => void,
): Cleanup {
  const group = root.querySelector<HTMLElement>(".lang-switch");
  if (!group) return () => {};
  const onClick = (e: Event) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>(".lang-btn");
    const next = btn?.dataset.locale as Locale | undefined;
    if (next && !btn?.classList.contains("active")) onChange(next);
  };
  group.addEventListener("click", onClick);
  return () => group.removeEventListener("click", onClick);
}
